import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import useCookie from 'react-use/lib/useCookie';
import useMedia from 'react-use/lib/useMedia';
import { addClassToElement, removeClassFromElement } from '../lib/classUtils';

import Logo from '@ux/logo';
import SidebarCollapse from '@ux/icon/sidebar-collapse';
import SidebarExpand from '@ux/icon/sidebar-expand';
import SidebarFooter from './SidebarFooter';
import SidebarNav from './SidebarNav';
import { useRootProps } from './UserDataProvider';
import getCookieHost from '../lib/getCookieHost';

import './Sidebar-ns.scss';
import './Sidebar.scss';

const FORCE_COLLAPSED_DURATION = 600;
const shortEnv = require('@ux/environments')('short');

const expires = new Date();
expires.setFullYear(expires.getFullYear() + 1);

const cookieOptions = {
  expires,
  path: '/',
  domain: getCookieHost(),
  secure: false
};

// eslint-disable-next-line complexity
const SidebarDesktop = ({
  navigation,
  logoHref,
  footerContent,
  isAfternic,
  isReseller,
  sidebarNavLinkHandler,
  primaryActiveNavLink,
  sidebarNavTopComponent
}) => {
  const aboveMobileStart = useMedia('(min-width: 992px)', true);

  const desktopNavigation = Array.isArray(navigation)
    ? navigation
      .filter(link => !link.mobileOnly)
      .map(link => ({
        ...link,
        children: link.children?.filter(child => !child.mobileOnly)
      }))
    : [];

  const [affixed, setAffixed] = useCookie('hub_sb');
  const isAffixed = affixed === '1';
  const notAffixed = affixed === '0';

  const [mouseEnter, setMouseEnter] = useState(false);
  const [forceCollapsed, setForceCollapsed] = useState(false);
  const [linkClicked, setLinkClicked] = useState(false);

  const toggleAffixed = useCallback(() => {
    if (affixed === '1') {
      setAffixed('0', cookieOptions);
      setForceCollapsed(true);
    } else {
      setAffixed('1', cookieOptions);
    }
  }, [affixed, setAffixed]);

  useEffect(() => {
    let timerHandle = null;
    if (forceCollapsed) {
      timerHandle = setTimeout(() => {
        setForceCollapsed(false);
      }, FORCE_COLLAPSED_DURATION);
    }
    // cleanup timer:
    return () => clearTimeout(timerHandle);
  }, [forceCollapsed]);

  useEffect(() => {
    if (affixed == null || typeof affixed === 'undefined') {
      setAffixed('1', cookieOptions);
      return;
    }

    if (affixed === '1' && aboveMobileStart) {
      addClassToElement('div.sidebar-wrapper', 'sidebar-affixed');
      document.body.classList.add('sidebar-locked');
    } else {
      removeClassFromElement('div.sidebar-wrapper', 'sidebar-affixed');
      document.body.classList.remove('sidebar-locked');
    }

    return () => {
      document.body.classList.remove('sidebar-locked');
    };
  }, [aboveMobileStart, affixed, setAffixed]);

  const { env, logos, privateLabelId } = useRootProps();
  const envShort = shortEnv(env) || 'dev';

  useEffect(() => {(linkClicked && setTimeout(setLinkClicked(false), FORCE_COLLAPSED_DURATION));}, [linkClicked]);

  const addRemoveHovered = forceCollapsed ? false : mouseEnter && notAffixed;
  const collapseSidebarOnLinkClick = notAffixed ? () => { (setLinkClicked(true), setMouseEnter(false)); } : null;

  return (
    <>
      <div
        className={ classNames('sidebar-wrapper',
          { 'hovered': addRemoveHovered,
            'sidebar-wrapper-shadow': isReseller && !isAfternic
          }) }
        onMouseEnter={ isAffixed ? null : () => setMouseEnter(true) }
        onMouseLeave={ isAffixed ? null : () => setMouseEnter(false) }>
        <div className={ classNames('sidebar-component') }>
          { aboveMobileStart && (
            <button type='button'
              className={ classNames('nav-toggle') }
              onClick={ toggleAffixed }
            >
              { isAffixed ? <SidebarCollapse /> : <SidebarExpand /> }
            </button>
          )}
          <Logo
            className='logo-anchor'
            collapsedLogo
            href={ envShort !== 'prod' && isAfternic ? `https://www.int.${envShort}-afternic.com` : logoHref }
            logos={ logos }
            privateLabelId={ privateLabelId }
            figClass={ isAfternic ? 'afternic-logo' : '' }
          />

          { sidebarNavTopComponent && (
            <div className='sidebar-nav-top-component-wrapper'>
              { sidebarNavTopComponent }
            </div>
          )}

          <ul className='sidebar-links'>
            <SidebarNav
              hovered={ mouseEnter && notAffixed }
              affixed={ affixed === '1' }
              navigation={ desktopNavigation }
              sidebarNavLinkHandler={ sidebarNavLinkHandler }
              primaryActiveNavLink={ primaryActiveNavLink }
              onClose={ collapseSidebarOnLinkClick }
            />
          </ul>
          { (Array.isArray(footerContent) && footerContent.length) ?
            <SidebarFooter sidebarNavLinkHandler={ sidebarNavLinkHandler } content={ footerContent } /> : null }
        </div>
      </div>
    </>
  );
};

SidebarDesktop.propTypes = {
  requestedHeader: PropTypes.symbol,
  logoHref: PropTypes.string,
  navigation: PropTypes.arrayOf(PropTypes.object),
  footerContent: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  isAfternic: PropTypes.bool,
  isReseller: PropTypes.bool,
  sidebarNavLinkHandler: PropTypes.func,
  primaryActiveNavLink: PropTypes.string,
  sidebarNavTopComponent: PropTypes.node,
  showHubBanner: PropTypes.bool
};

export default SidebarDesktop;
